import React, { useCallback, useEffect, useRef } from "react";
import { apiDomain } from "configs/configs";
import {
  Select,
  Flex,
  Input,
  Form,
  Button,
  Grid,
  Upload,
  Modal,
  UploadFile,
} from "antd";
import { Modal as MobileModal } from "antd-mobile";
import { UploadOutlined } from "@ant-design/icons";
import { UploadChangeParam } from "antd/es/upload/interface";
import { create } from "zustand";
import {
  MeasureType,
  MeasureGroupType,
} from "pages/admin/objectsAndClients/objects/types";

const { useBreakpoint } = Grid;

type ComponentProps = {
  measures: MeasureType[];
  setMeasures: (update: (state: MeasureType[]) => MeasureType[]) => void;
  isAddGroupMode: boolean;
  setIsAddGroupMode: (isAddGroupMode: boolean) => void;
  isEditGroupMode: string | undefined;
  setIsEditGroupMode: (isEditGroupMode?: string) => void;
};

type OperationsStoreState = {
  isFileUploading: boolean;
  setIsFileUploading: (isError: boolean) => void;
  previewFile?: UploadFile;
  setPreviewFile: (previewFile?: UploadFile) => void;
  isSubmittable: boolean;
  setIsSubmittable: (isSubmittable: boolean) => void;
};

const useOperationsStore = create<OperationsStoreState>((set) => ({
  isFileUploading: false,
  setIsFileUploading: (isFileUploading) => set({ isFileUploading }),
  previewFile: undefined,
  setPreviewFile: (previewFile?: UploadFile) => set({ previewFile }),
  isSubmittable: false,
  setIsSubmittable: (isSubmittable) => set({ isSubmittable }),
}));

const Component: React.FC<ComponentProps> = ({
  measures,
  setMeasures,
  isAddGroupMode,
  setIsAddGroupMode,
  isEditGroupMode,
  setIsEditGroupMode,
}) => {
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const measuresRef = useRef(measures);
  const valuesRef = useRef(values);

  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  //  Save current list to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  //  Save current list to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    measuresRef.current = measures;
  }, [measures]);

  const {
    isFileUploading,
    setIsFileUploading,
    previewFile,
    setPreviewFile,
    isSubmittable,
    setIsSubmittable,
  } = useOperationsStore((store) => store);

  //  Triggers when isEditMode changed. Set initial fields value in form or reset it. Reset error.
  useEffect(() => {
    const { group, building } = measuresRef.current.reduce<{
      group: MeasureGroupType | undefined;
      building: MeasureType | undefined;
    }>(
      (result, building) => {
        if (result.group) return result;

        const foundGroup = building.groups?.find((group) => {
          return group.id.toString() === isEditGroupMode;
        });

        if (foundGroup) {
          return { group: foundGroup, building };
        }

        return result;
      },
      { group: undefined, building: undefined }
    );

    if (!group || !building) form.resetFields();

    if (group && building) {
      const file = group.user_file
        ? [
            {
              uid: group.user_file.id,
              name: group.user_file.file_name,
              size: group.user_file.size,
              type: group.user_file.mime_type,
              response: [
                {
                  file_name: group.user_file.file_name,
                  file_path: group.user_file.file_path,
                  mime_type: group.user_file.mime_type,
                  size: group.user_file.size,
                  id: group.user_file.id,
                },
              ],
              thumbUrl: `${apiDomain}/userFiles/${group.user_file.file_path}`,
            },
          ]
        : [];

      form.setFieldsValue({
        building: building.id,
        name: group.name,
        file: file,
      });
    }

    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [isEditGroupMode, form, measuresRef, valuesRef, setIsSubmittable]);

  //  Effect triggers when mode changes to edit or add. Also when some value changes. It fires validation and reset error.
  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [form, values, setIsSubmittable]);

  //  Submit form, depends of mode
  const handleFinish = useCallback(() => {
    try {
      const building = values.building.toString();
      const name = values.name.trim();
      const fileId = values?.file?.[0]?.response?.[0]?.id
        ? values?.file?.[0]?.response[0].id
        : values?.file?.[0]?.uid && !isNaN(Number(values?.file?.[0].uid))
        ? Number(values?.file?.[0].uid)
        : null;

      const userFile = fileId
        ? {
            id: fileId,
            file_name: values?.file?.[0]?.response?.[0]?.file_name
              ? values?.file?.[0]?.response[0].file_name
              : values?.file?.[0]?.name
              ? values?.file?.[0].name
              : null,
            file_path: values?.file?.[0]?.response?.[0]?.file_path
              ? values?.file?.[0]?.response[0].file_path
              : null,
            mime_type: values?.file?.[0]?.response?.[0]?.mime_type
              ? values?.file?.[0]?.response[0].mime_type
              : values?.file?.[0]?.type
              ? values?.file?.[0].type
              : null,
            size: values?.file?.[0]?.response?.[0]?.size
              ? values?.file?.[0]?.response[0].size
              : values?.file?.[0]?.size
              ? values?.file?.[0].size
              : null,
          }
        : undefined;

      setMeasures((measures) => {
        if (isEditGroupMode) {
          return measures.map((measure) => {
            //  Remove from not current buildings
            if (measure.id.toString() !== building) {
              return {
                ...measure,
                groups: measure.groups
                  ? measure.groups.filter(
                      (group) => group.id.toString() !== isEditGroupMode
                    )
                  : undefined,
              };
            }

            return {
              ...measure,
              groups: measure.groups
                ? measure.groups.find(
                    (group) => group.id.toString() === isEditGroupMode
                  )
                  ? measure.groups.map((groupItem) => {
                      if (groupItem.id.toString() === isEditGroupMode)
                        return {
                          ...groupItem,
                          name: name,
                          user_file_id: fileId,
                          user_file: userFile,
                        };

                      return groupItem;
                    })
                  : [
                      ...measure.groups,
                      {
                        id: isEditGroupMode,
                        name: name,
                        user_file_id: fileId,
                        user_file: userFile,
                      },
                    ]
                : [
                    {
                      id: isEditGroupMode,
                      name: name,
                      user_file_id: fileId,
                      user_file: userFile,
                    },
                  ],
            };
          });
        }

        const maxId = measures
          .flatMap((measure) => measure.groups || [])
          .filter((group) => group.id.toString()?.startsWith("newGroup-"))
          .map((group) => parseInt(group.id.toString().split("-")[1], 10))
          .reduce((max, current) => Math.max(max, current), 0);

        const newId = `newGroup-${maxId + 1}`;

        return measures.map((measure) => {
          if (measure.id.toString() === building)
            return {
              ...measure,
              groups: [
                ...(measure.groups ? measure.groups : []),
                {
                  id: newId,
                  name: name,
                  user_file_id: fileId,
                  user_file: userFile,
                  is_children_visible: true,
                },
              ],
            };

          return measure;
        });
      });
    } catch (error) {
      console.log(error);
    }

    if (isEditGroupMode) setIsEditGroupMode(undefined);
    if (isAddGroupMode) setIsAddGroupMode(false);
  }, [
    setMeasures,
    values,
    isAddGroupMode,
    setIsAddGroupMode,
    isEditGroupMode,
    setIsEditGroupMode,
  ]);

  const handleUploadFileChange = useCallback(
    (info: UploadChangeParam) => {
      setIsFileUploading(info.file.status === "uploading");
    },
    [setIsFileUploading]
  );

  const handlePreview = useCallback(
    async (previewFile: UploadFile) => {
      setPreviewFile(previewFile);
    },
    [setPreviewFile]
  );

  return (
    <>
      <Flex vertical gap={16}>
        <Form
          layout={"vertical"}
          form={form}
          onFinish={handleFinish}
          initialValues={{
            name: "",
            file: null,
          }}
        >
          <Form.Item
            name="building"
            label="Помещение"
            rules={[
              {
                required: true,
                message: "Пожалуйста, выберите помещение!",
              },
            ]}
            validateTrigger={false}
            required={false}
          >
            <Select placeholder="Выберите помещение">
              {measures.map((measure, index) => (
                <Select.Option key={index} value={measure.id}>
                  {measure.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="name"
            label="Наименование"
            rules={[
              {
                required: true,
                message: "Пожалуйста, введите наименование!",
              },
            ]}
            validateTrigger={false}
            required={false}
          >
            <Input size="large" name="name" />
          </Form.Item>
          <Form.Item
            label="Добавить фото или чертеж"
            name="file"
            valuePropName="fileList"
            getValueFromEvent={(e) => e.fileList}
          >
            <Upload
              action={`${apiDomain}/api/upload-file`}
              accept="image/*"
              maxCount={1}
              withCredentials={true}
              onChange={handleUploadFileChange}
              onPreview={handlePreview}
              listType="picture-card"
            >
              <Button icon={<UploadOutlined />} disabled={isFileUploading}>
                {isFileUploading ? `Идет загрузка` : `Загрузить файл`}
              </Button>
            </Upload>
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              size="large"
              disabled={!isSubmittable || isFileUploading}
              htmlType="submit"
            >
              Сохранить
            </Button>
          </Form.Item>
        </Form>
      </Flex>
      {isMobile && (
        <MobileModal
          visible={!!previewFile?.url || !!previewFile?.thumbUrl}
          content={
            <img
              alt={previewFile?.fileName}
              style={{ width: "100%" }}
              src={
                previewFile?.url
                  ? previewFile?.url
                  : `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                  ? `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                  : previewFile?.thumbUrl
              }
              onClick={() => setPreviewFile(undefined)}
            />
          }
          closeOnMaskClick={true}
          showCloseButton={true}
          onClose={() => setPreviewFile(undefined)}
        />
      )}
      {!isMobile && (
        <Modal
          open={!!previewFile?.url || !!previewFile?.thumbUrl}
          title={previewFile?.fileName}
          footer={null}
          onCancel={() => setPreviewFile(undefined)}
        >
          <img
            alt={previewFile?.fileName}
            style={{ width: "100%" }}
            src={
              previewFile?.url
                ? previewFile?.url
                : `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                ? `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                : previewFile?.thumbUrl
            }
            onClick={() => setPreviewFile(undefined)}
          />
        </Modal>
      )}
    </>
  );
};

export default Component;
